import { Faq as FaqType } from 'libs/http/api/content/content.types';

import { type FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';
import type { NumberedFeatureCard } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards.types';
import type { NumberedStatRow } from 'ui/organisms/NumberedStatsSection/NumberedStatsSection.types';
import type { PillarCardItem } from 'ui/organisms/PillarCards/PillarCards.types';

export const AI_PRODUCT_DIFFS: PillarCardItem[] = [
  {
    title: 'Live Data, Not Training Data',
    description:
      "ChatGPT and Claude rely on what they learned during training. Regis queries 600M+ company profiles from official registries in real time. Every answer reflects today's data.",
  },
  {
    title: 'Every Answer Has a Source',
    description:
      'No hallucinations. Every data point comes with the registry name, collection date, and a direct link. Built for compliance teams and regulated industries.',
  },
  {
    title: 'Export, Integrate, Automate',
    description:
      "Don't just read answers — use them. Export lists to CSV, push data to your CRM, or connect via API. Regis is a workflow tool, not just a chatbot.",
  },
];

export const AI_PRODUCT_STATS: FeatureGridItem[] = [
  { value: '600', suffix: 'M+', title: 'Company Profiles' },
  { value: '400', suffix: '+', title: 'Government Registries' },
  { value: '200', suffix: '+', title: 'Countries Covered' },
  { value: '24', suffix: 'hr', title: 'Data Refresh Cycle' },
];

export const AI_PRODUCT_STEPS: NumberedStatRow[] = [
  {
    n: '01',
    title: 'Type a question in plain English',
    body: "Ask about any company, any country, any metric. The same way you'd message a colleague. No query language, no forms, no dropdowns.",
  },
  {
    n: '02',
    title: 'Regis searches live registry data',
    body: 'Your question triggers a real-time search across 600M+ company profiles sourced from 400+ official government registries. Not cached. Not estimated. Live.',
  },
  {
    n: '03',
    title: 'Get answers with sources. Ask follow-ups.',
    body: 'Every response includes source attribution and timestamps. Keep the conversation going — Regis remembers context. Export to CSV, build lists, generate reports.',
  },
];

export const AI_PRODUCT_PROBLEMS: NumberedFeatureCard[] = [
  {
    num: '01',
    title: 'Hallucinated answers',
    description:
      "LLMs invent revenue figures, director names, and filings that don't exist. Confident tone. Zero accuracy.",
  },
  {
    num: '02',
    title: 'No private company data',
    description: "99% of companies are private. ChatGPT can't access their financials. You get a refusal or a guess.",
  },
  {
    num: '03',
    title: 'Outdated information',
    description:
      "Training data is months or years old. Companies dissolve, restructure, change directors — the model doesn't know.",
  },
  {
    num: '04',
    title: 'Sourced from the open web',
    description:
      'LLMs pull from publicly available websites, news articles, and Wikipedia — not official government registries. No filings. No structured records. No verified data.',
  },
  {
    num: '05',
    title: "Can't build prospect lists",
    description:
      'No database behind it. Ask for "companies in Germany with €50M+ revenue" and you get 5 famous brands, not 200+ real matches.',
  },
  {
    num: '06',
    title: 'No sources or audit trail',
    description:
      'No registry citation. No filing date. No way to verify. "Based on my training data" isn\'t a source your compliance team can use.',
  },
];

export const AI_PRODUCT_FAQ: FaqType[] = [
  {
    id: 'data-freshness',
    question: 'How fresh is the registry data?',
    answer:
      "Regis returns the latest filing available from each source, not a cached snapshot. Update frequency follows each registry's own publishing cadence — some update in near real time, others on a set schedule. Corporate bulk deliveries are refreshed quarterly.",
  },
  {
    id: 'data-source',
    question: 'Where does the data come from?',
    answer:
      'Primary, government-sourced records — drawn directly from 400+ official government registries across 200+ countries, covering 600M+ companies. Nothing is estimated or pulled from the open web.',
  },
  {
    id: 'hallucinations',
    question: 'Does Regis hallucinate — and can I verify the source?',
    answer:
      'No hallucinations: Regis answers only from official registry data, never the open web. And every answer carries its registry source, so you can trace and audit each fact yourself — evidence, not guesses.',
  },
  {
    id: 'coverage',
    question: "What's covered — countries, entities, history?",
    answer:
      '600M+ companies across 200+ countries: status, registered details, shareholders, directors, group structure, corporate linkage and up to 20 years of filed financials where available.',
  },
  {
    id: 'languages',
    question: 'What languages can I use?',
    answer: 'Ask any company question in any language. Regis understands the question and returns the answer with its source.',
  },
  {
    id: 'pricing',
    question: 'How does pricing work?',
    answer:
      "Every plan includes every data module — you're metered in credits, so you only pay for what you use. Free is 5 credits a month; Pro is 200, plus top-ups and priority support; Corporate is unlimited.",
  },
  {
    id: 'download-api',
    question: 'Can I download data or connect the API?',
    answer:
      'CSV and PDF download, the API, and bulk delivery are available on Corporate. Free and Pro let you view and work with answers on screen.',
  },
  {
    id: 'vs-general-llm',
    question: 'How is Regis different from Claude or ChatGPT?',
    answer:
      "General LLMs answer from training data — the open web, frozen at a point in time. They can't see live company records, so they confidently invent ownership, financials and registration numbers. Regis answers only from live, official government registries, with the source attached to every answer. The same ease of just asking — real, verifiable data underneath.",
  },
  {
    id: 'vs-legacy-providers',
    question: "How is Regis different from D&B or Moody's?",
    answer:
      "The underlying records overlap, but the model doesn't. Legacy providers deliver data through partners, behind long contracts and per-seat licences that take weeks to deploy — and often can't show you the original source. Regis is primary, government-sourced, straight from the registry: ask in plain language, get a sourced answer in seconds, starting free.",
  },
  {
    id: 'vs-claude-subscription',
    question: 'I already pay for Claude — why pay for Regis too?',
    answer:
      'Use both. Claude is excellent at reasoning, but it has no live access to 600M+ companies across 400+ government registries, so on company facts it guesses. Regis connects to Claude through our connector, giving it real, sourced company data on demand — so the answer is right and auditable, not invented.',
  },
];
